import { PricingUnit, PricePrecision } from "@/types";
import { ColumnDef } from "@tanstack/react-table";
import {
    regex,
    makeCellWithRegexSorter,
    expr,
    transformAllDataTables,
    resolvePricePrecision,
} from "./shared";
import { CostDuration } from "@/types";
import RegionLinkPreloader from "@/components/RegionLinkPreloader";
import exprCompiler from "@/utils/expr";

export interface GCPPricing {
    [region: string]: {
        [platform: string]: {
            ondemand: number;
            spot?: number;
            // Resource-based committed use discount (CUD) hourly prices, sourced
            // from the catalog's committed-use SKUs. Present only on the linux
            // platform and only when the machine family/region has a CUD SKU.
            cud_1yr?: number;
            cud_3yr?: number;
        };
    };
}

export type GCPInstance = {
    instance_type: string;
    pretty_name: string;
    pricing: GCPPricing;
    vCPU: number;
    memory: number;
    GPU: number;
    GPU_model?: string;
    GPU_memory?: number;
    family: string;
    network_performance: string;
    generation: string;
    local_ssd: boolean;
    local_ssd_size?: number;
    shared_cpu: boolean;
    compute_optimized?: boolean;
    memory_optimized?: boolean;
    accelerator_type?: string;
};

const initialColumnsArr = [
    ["pretty_name", true],
    ["instance_type", true],
    ["memory", true],
    ["vCPU", true],
    ["memory_per_vcpu", false],
    ["GPU", false],
    ["network_performance", false],
    ["generation", false],
    ["local_ssd", false],
    ["shared_cpu", false],
    ["linux-ondemand", true],
    ["linux-1yr-cud", true],
    ["linux-3yr-cud", true],
    ["linux-spot", true],
    ["windows-ondemand", true],
    ["windows-spot", true],
] as const;

export const initialColumnsValue: {
    [idx in (typeof initialColumnsArr)[number][0]]: boolean;
} = {} as any;
for (const [key, value] of initialColumnsArr) {
    initialColumnsValue[key] = value;
}

export function transformDataTables(dataTablesData: any) {
    return transformAllDataTables(initialColumnsArr, dataTablesData);
}

export function makePrettyNames<V>(
    makeColumnOption: (
        key: keyof typeof initialColumnsValue,
        label: string,
    ) => V,
    _reservedTerm: string,
) {
    return [
        makeColumnOption("pretty_name", "Instance Name"),
        makeColumnOption("instance_type", "API Name"),
        makeColumnOption("memory", "Instance Memory"),
        makeColumnOption("vCPU", "vCPUs"),
        makeColumnOption("memory_per_vcpu", "Memory per vCPU"),
        makeColumnOption("GPU", "GPUs"),
        makeColumnOption("network_performance", "Network Performance"),
        makeColumnOption("generation", "Generation"),
        makeColumnOption("local_ssd", "Local SSD"),
        makeColumnOption("shared_cpu", "Shared CPU"),
        makeColumnOption("linux-ondemand", "Linux On-Demand"),
        makeColumnOption("linux-1yr-cud", "Linux 1-Year CUD"),
        makeColumnOption("linux-3yr-cud", "Linux 3-Year CUD"),
        makeColumnOption("linux-spot", "Linux Spot"),
        makeColumnOption("windows-ondemand", "Windows On-Demand"),
        makeColumnOption("windows-spot", "Windows Spot"),
    ];
}

function round(value: number) {
    return Math.round(value * 100) / 100;
}

function calculateCost(
    price: string | number | undefined,
    instance: GCPInstance,
    pricingUnit: PricingUnit,
    costDuration: CostDuration,
    usdRate: number,
): number {
    if (!price) return -1;

    const hourMultipliers = {
        secondly: 1 / (60 * 60),
        minutely: 1 / 60,
        hourly: 1,
        daily: 24,
        weekly: 7 * 24,
        monthly: (365 * 24) / 12,
        annually: 365 * 24,
    };

    const durationMultiplier = hourMultipliers[costDuration];
    let pricingUnitModifier = 1;

    if (pricingUnit !== "instance") {
        pricingUnitModifier = instance[
            pricingUnit === "vcpu" ? "vCPU" : "memory"
        ] as number;
    }

    return (
        ((Number(price) * durationMultiplier) / pricingUnitModifier) * usdRate
    );
}

export function calculateAndFormatCost(
    price: string | number | undefined,
    instance: GCPInstance,
    pricingUnit: PricingUnit,
    costDuration: CostDuration,
    currency: {
        code: string;
        usdRate: number;
    },
    pricePrecision: PricePrecision = "auto",
): string | undefined {
    const perTime = calculateCost(
        price,
        instance,
        pricingUnit,
        costDuration,
        currency.usdRate,
    );
    if (perTime === -1) return undefined;

    const precision = resolvePricePrecision(pricePrecision, costDuration);

    const measuringUnits = {
        instances: "",
        vcpu: "vCPU",
        ecu: "vCPU",
        memory: "GiB",
        gpu_memory: "GiB",
    };

    let durationText: string = costDuration;
    if (costDuration === "secondly") durationText = "per sec";
    if (costDuration === "minutely") durationText = "per min";

    const pricingMeasuringUnits =
        pricingUnit === "instance"
            ? ` ${durationText}`
            : ` ${durationText} / ${measuringUnits[pricingUnit]}`;

    const currencyData = Intl.NumberFormat("en-US", {
        style: "currency",
        currency: currency.code,
        minimumFractionDigits: precision,
        maximumFractionDigits: precision,
    }).format(perTime);

    return `${currencyData}${pricingMeasuringUnits}`;
}

function getPricingSorter(
    selectedRegion: string,
    pricingUnit: PricingUnit,
    costDuration: CostDuration,
    pricePrecision: PricePrecision,
    getter: (pricing: GCPPricing[string] | undefined) => number | undefined,
    currency: {
        code: string;
        usdRate: number;
    },
) {
    return {
        sortingFn: (rowA, rowB) => {
            const valueA = calculateCost(
                getter(rowA.original.pricing?.[selectedRegion]),
                rowA.original,
                pricingUnit,
                costDuration,
                currency.usdRate,
            );
            const valueB = calculateCost(
                getter(rowB.original.pricing?.[selectedRegion]),
                rowB.original,
                pricingUnit,
                costDuration,
                currency.usdRate,
            );
            return valueA - valueB;
        },
        sortUndefined: "last",
        accessorFn: (row) => {
            const g = getter(row.pricing?.[selectedRegion]);
            if (isNaN(Number(g))) return undefined;
            const value = calculateCost(
                g,
                row,
                pricingUnit,
                costDuration,
                currency.usdRate,
            );
            return value === -1 ? undefined : value;
        },
        ...makeCellWithRegexSorter("pricing", (info) => {
            const pricing = info.row.original.pricing;
            const price = getter(pricing?.[selectedRegion]);
            if (isNaN(Number(price))) return undefined;
            return calculateAndFormatCost(
                price,
                info.row.original,
                pricingUnit,
                costDuration,
                currency,
                pricePrecision,
            );
        }),
    } satisfies Partial<ColumnDef<GCPInstance>>;
}

export const columnsGen = (
    selectedRegion: string,
    pricingUnit: PricingUnit,
    costDuration: CostDuration,
    pricePrecision: PricePrecision,
    _reservedTerm: string, // GCP doesn't use reserved terms, but we need to match the signature
    currency: {
        code: string;
        usdRate: number;
        cnyRate: number;
    },
): ColumnDef<GCPInstance>[] => {
    return [
        {
            accessorKey: "pretty_name",
            id: "pretty_name",
            header: "Instance Name",
            sortingFn: "alphanumeric",
        },
        {
            accessorKey: "instance_type",
            id: "instance_type",
            header: "API Name",
            sortingFn: "alphanumeric",
            cell: (info) => {
                const value = info.getValue() as string;
                return (
                    <RegionLinkPreloader
                        onClick={(e) => e.stopPropagation()}
                        href={`/gcp/${value}`}
                    >
                        {info.row.original.instance_type}
                    </RegionLinkPreloader>
                );
            },
            filterFn: regex({ accessorKey: "instance_type" }),
        },
        {
            accessorKey: "memory",
            header: "Instance Memory",
            size: 160,
            id: "memory",
            sortingFn: "alphanumeric",
            filterFn: expr,
            cell: (info) => `${info.getValue() as number} GiB`,
        },
        {
            accessorKey: "vCPU",
            header: "vCPUs",
            size: 160,
            id: "vCPU",
            sortingFn: "alphanumeric",
            filterFn: expr,
        },
        {
            accessorKey: "memory",
            header: "Memory per vCPU",
            size: 160,
            id: "memory_per_vcpu",
            filterFn: (row, _, filterValue) => {
                const value = row.original.memory;
                const cpu = row.original.vCPU;
                const memoryPerVcpu = round(value / cpu);
                try {
                    return exprCompiler(filterValue)(
                        memoryPerVcpu,
                        `${memoryPerVcpu} GiB/vCPU`,
                    );
                } catch {
                    return true;
                }
            },
            cell: (info) => {
                const value = info.getValue() as number;
                const cpu = info.row.original.vCPU;
                return `${round(value / cpu)} GiB/vCPU`;
            },
        },
        {
            accessorKey: "GPU",
            header: "GPUs",
            size: 160,
            id: "GPU",
            filterFn: expr,
        },
        {
            accessorKey: "network_performance",
            header: "Network Performance",
            size: 160,
            id: "network_performance",
            sortingFn: "alphanumeric",
            filterFn: regex({ accessorKey: "network_performance" }),
            cell: (info) => info.getValue() as string,
        },
        {
            accessorKey: "generation",
            header: "Generation",
            size: 160,
            id: "generation",
            sortingFn: "alphanumeric",
            filterFn: regex({ accessorKey: "generation" }),
            cell: (info) => info.getValue() as string,
        },
        {
            accessorKey: "local_ssd",
            header: "Local SSD",
            size: 120,
            id: "local_ssd",
            filterFn: (row, _, filterValue) => {
                const value = row.original.local_ssd;
                try {
                    return exprCompiler(filterValue)(
                        value ? 1 : 0,
                        value ? "Yes" : "No",
                    );
                } catch {
                    return true;
                }
            },
            cell: (info) => ((info.getValue() as boolean) ? "Yes" : "No"),
        },
        {
            accessorKey: "shared_cpu",
            header: "Shared CPU",
            size: 120,
            id: "shared_cpu",
            filterFn: (row, _, filterValue) => {
                const value = row.original.shared_cpu;
                try {
                    return exprCompiler(filterValue)(
                        value ? 1 : 0,
                        value ? "Yes" : "No",
                    );
                } catch {
                    return true;
                }
            },
            cell: (info) => ((info.getValue() as boolean) ? "Yes" : "No"),
        },
        {
            accessorKey: "pricing",
            header: "Linux On Demand cost",
            id: "linux-ondemand",
            ...getPricingSorter(
                selectedRegion,
                pricingUnit,
                costDuration,
                pricePrecision,
                (pricing) => pricing?.linux?.ondemand,
                currency,
            ),
        },
        {
            accessorKey: "pricing",
            header: "Linux 1-Year CUD cost",
            id: "linux-1yr-cud",
            ...getPricingSorter(
                selectedRegion,
                pricingUnit,
                costDuration,
                pricePrecision,
                (pricing) => pricing?.linux?.cud_1yr,
                currency,
            ),
        },
        {
            accessorKey: "pricing",
            header: "Linux 3-Year CUD cost",
            id: "linux-3yr-cud",
            ...getPricingSorter(
                selectedRegion,
                pricingUnit,
                costDuration,
                pricePrecision,
                (pricing) => pricing?.linux?.cud_3yr,
                currency,
            ),
        },
        {
            accessorKey: "pricing",
            header: "Linux Spot cost",
            id: "linux-spot",
            ...getPricingSorter(
                selectedRegion,
                pricingUnit,
                costDuration,
                pricePrecision,
                (pricing) => pricing?.linux?.spot,
                currency,
            ),
        },
        {
            accessorKey: "pricing",
            header: "Windows On Demand cost",
            id: "windows-ondemand",
            ...getPricingSorter(
                selectedRegion,
                pricingUnit,
                costDuration,
                pricePrecision,
                (pricing) => pricing?.windows?.ondemand,
                currency,
            ),
        },
        {
            accessorKey: "pricing",
            header: "Windows Spot cost",
            id: "windows-spot",
            ...getPricingSorter(
                selectedRegion,
                pricingUnit,
                costDuration,
                pricePrecision,
                (pricing) => pricing?.windows?.spot,
                currency,
            ),
        },
    ];
};
